import React, { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Searchable combobox using shadcn Command + Popover.
 * options: [{value, label, sub?}]
 * Optional `allowCustom`: type a value not in the list and press Enter to use it.
 */
export default function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = "Select…",
  testid,
  emptyText = "No results.",
  allowCustom = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = options.find((o) => o.value === value);

  const display = selected ? selected.label : (value && allowCustom ? value : "");

  const commitCustom = () => {
    const v = query.trim();
    if (!v) return;
    onChange(v);
    setQuery("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { if (!disabled) setOpen(o); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className="w-full flex items-center justify-between border border-slate-300 bg-white rounded-md px-3 py-2 text-sm hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 disabled:bg-slate-50 disabled:text-slate-500"
        >
          <span className={cn("truncate", !display && "text-slate-400")}>
            {display || placeholder}
          </span>
          <ChevronsUpDown size={14} className="text-slate-500 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command shouldFilter>
          <CommandInput
            placeholder={allowCustom ? "Search or type custom…" : "Search…"}
            value={query}
            onValueChange={setQuery}
            onKeyDown={(e) => {
              if (allowCustom && e.key === "Enter" && query.trim()) {
                e.preventDefault();
                commitCustom();
              }
            }}
          />
          <CommandList>
            <CommandEmpty>
              {allowCustom && query.trim() ? (
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm text-[#1D2951] inline-flex items-center gap-2 hover:bg-slate-50"
                  onClick={commitCustom}
                >
                  <Plus size={14} /> Use “{query.trim()}”
                </button>
              ) : (
                emptyText
              )}
            </CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${o.sub || ""}`}
                  onSelect={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === o.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex-1">
                    <div className="text-sm">{o.label}</div>
                    {o.sub && (
                      <div className="text-[11px] text-slate-500 font-mono">{o.sub}</div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
